import React, { useRef, useState } from "react";
import Page from "../components/Page";
import { IconCarrot, IconDownload, IconFilter, IconTrendingUp, IconTrendingDown, IconGift, IconReceipt2, IconWallet, IconScale, IconBuildingBank, IconCalculator, IconFileSpreadsheet, IconReportMoney, IconReceipt, IconDiscount2 } from "@tabler/icons-react";

import { iconStroke } from "../config/config";
import { useReports } from "../controllers/reports.controller"
import { CURRENCIES } from "../config/currencies.config";
import ReportPaymentTypePieChart from "../components/ReportPaymentTypePieChart";
import StatusPieChart from "../components/StatusPieChart";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import { toast } from "react-hot-toast";
import ReportExportWrapper from "../components/ReportExportWrapper";
import ReportsDirectoryGrid from "../components/ReportsDirectoryGrid";

export default function ReportsPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const filters = [
    { key: "today", value: t("reports.filters.today") },
    { key: "yesterday", value: t("reports.filters.yesterday") },
    { key: "last_7days", value: t("reports.filters.last_7days") },
    { key: "this_month", value: t("reports.filters.this_month") },
    { key: "last_month", value: t("reports.filters.last_month") },
    { key: "custom", value: t("reports.filters.custom") },
  ];

  const fromDateRef = useRef();
  const toDateRef = useRef();
  const filterTypeRef = useRef();

  const now = new Date();
  const defaultDateFrom = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
  const defaultDateTo = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;

  const [state, setState] = useState({
    filter: filters[3].key, // Default to this month
    fromDate: null,
    toDate: null,
  });

  const {APIURL, data, error, isLoading} = useReports({
    type: state.filter,
    from: state.fromDate,
    to: state.toDate,
  });

  if(isLoading) {
    return <Page>
      <div className="flex items-center justify-center h-full">
        <span className="loading loading-spinner loading-lg text-restro-green"></span>
      </div>
    </Page>
  }

  if(error) {
    console.error(error);
    return <Page>
      <div className="text-center py-20">
        <p className="text-red-500 font-bold">{t("reports.error_message")}</p>
      </div>
    </Page>;
  }

  const {
    ordersCount, newCustomers, repeatedCustomers, currency:currencyCode, averageOrderValue, totalCustomers, netRevenue, taxTotal, serviceChargeTotal, topSellingItems, revenueTotal, totalPaymentsByPaymentTypes,
    invoiceStats, complementaryTotal, purchaseStats
  } = data;

  const currency = CURRENCIES.find((c)=>c.cc==currencyCode)?.symbol || "$";

  const btnExportCSV = async () => {
    try {
      const { Parser } = await import("@json2csv/plainjs");
      const { saveAs } = await import("file-saver");

      const summaryData = [{
        "Orders": ordersCount ?? 0,
        "New Customers": newCustomers ?? 0,
        "Repeated Customers": repeatedCustomers ?? 0,
        "Avg. Order Value": Number(String(averageOrderValue ?? 0).replace(/,/g, "")).toFixed(2),
        "Total Customers": totalCustomers ?? 0,
        "Net Revenue": Number(String(netRevenue ?? 0).replace(/,/g, "")).toFixed(2),
        "Tax Total": Number(String(taxTotal ?? 0).replace(/,/g, "")).toFixed(2),
        "Service Charge Total": Number(String(serviceChargeTotal ?? 0).replace(/,/g, "")).toFixed(2),
        "Revenue Total": Number(String(revenueTotal ?? 0).replace(/,/g, "")).toFixed(2),
        "Total Due": Number(String(invoiceStats?.total_due ?? 0).replace(/,/g, "")).toFixed(2),
        "Total Discount": Number(String(invoiceStats?.total_discount ?? 0).replace(/,/g, "")).toFixed(2),
        "Complementary Total": Number(String(complementaryTotal ?? 0).replace(/,/g, "")).toFixed(2),
        "Paid Purchases": Number(String(purchaseStats?.total_paid_purchase ?? 0).replace(/,/g, "")).toFixed(2),
        "Due Purchases": Number(String(purchaseStats?.total_due_purchase ?? 0).replace(/,/g, "")).toFixed(2),
      }];

      const topSellingItemsData = (topSellingItems || []).map(item => ({
        "Item Title": item.title || '',
        "Item Price": Number(String(item.price ?? 0).replace(/,/g, "")).toFixed(2),
        "Orders Count": item.orders_count ?? 0,
      }));


      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const fileName = `Reports_${state.filter}_${new Date().toISOString().split('T')[0]}.csv`;

      saveAs(blob, fileName);
      toast.success(t("reports.saved_successfully"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("reports.error_saving_file"));
    }
  };

  const columns = [
    { header: "Item Title", key: "title" },
    { header: "Price", key: "price" },
    { header: "Sold Quantity", key: "orders_count" }
  ];

  const tableData = (topSellingItems || []).map(item => ({
    title: item.title || '',
    price: currency + Number(String(item.price ?? 0).replace(/,/g, "")).toFixed(2),
    orders_count: item.orders_count ?? 0
  }));

  const summaryData = {
    "Orders": ordersCount ?? 0,
    "New Customers": newCustomers ?? 0,
    "Repeated Customers": repeatedCustomers ?? 0,
    "Avg. Order Value": currency + Number(String(averageOrderValue ?? 0).replace(/,/g, "")).toFixed(2),
    "Total Customers": totalCustomers ?? 0,
    "Net Revenue": currency + Number(String(netRevenue ?? 0).replace(/,/g, "")).toFixed(2),
    "Tax Total": currency + Number(String(taxTotal ?? 0).replace(/,/g, "")).toFixed(2),
    "Service Charge Total": currency + Number(String(serviceChargeTotal ?? 0).replace(/,/g, "")).toFixed(2),
    "Revenue Total": currency + Number(String(revenueTotal ?? 0).replace(/,/g, "")).toFixed(2),
    "Total Due": currency + Number(String(invoiceStats?.total_due ?? 0).replace(/,/g, "")).toFixed(2),
    "Total Discount": currency + Number(String(invoiceStats?.total_discount ?? 0).replace(/,/g, "")).toFixed(2)
  };

  return (
    <Page>
      <ReportExportWrapper
        title={t("reports.title")}
        reportType="General Revenue & Sales"
        dateRange={filters.find(f=>f.key==state.filter)?.value}
        filters={state.filter === "custom" ? `${state.fromDate} to ${state.toDate}` : "None"}
        columns={columns}
        tableData={tableData}
        summaryData={summaryData}
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 print:hidden">
          <div>
            <h3 className="text-3xl font-black">{t("reports.title")}</h3>
            <p className="text-gray-500 text-sm">{t("reports.showing_data_for", { filter: filters.find(f=>f.key==state.filter)?.value })}</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => document.getElementById("filter-dialog").showModal()}
              className="btn btn-ghost bg-restro-gray border border-restro-border-green rounded-2xl gap-2"
            >
              <IconFilter stroke={iconStroke} size={20} /> {t("reports.filters_button")}
            </button>
          </div>
        </div>

        {/* Reports & Analytics Grid View Directory */}
        <ReportsDirectoryGrid />

        {/* Section Divider: General Revenue & Performance Overview */}
        <div className="flex items-center gap-3 my-8 pt-6 border-t border-gray-200 dark:border-gray-800 print:hidden">
          <div className="h-2.5 w-2.5 rounded-full bg-restro-green"></div>
          <h3 className="text-xl font-black text-restro-text">Revenue Performance & Sales Analytics</h3>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Main Stats */}
        <div className="p-6 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] shadow-sm">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t("reports.revenue")}</p>
          <h2 className="text-3xl font-black text-restro-green">{currency}{Number(String(revenueTotal).replace(/,/g, "")).toLocaleString()}</h2>
        </div>
        <div className="p-6 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] shadow-sm">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Due (AR)</p>
          <h2 className="text-3xl font-black text-blue-600">{currency}{Number(String(invoiceStats?.total_due || 0).replace(/,/g, "")).toLocaleString()}</h2>
        </div>
        <div className="p-6 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] shadow-sm cursor-pointer hover:bg-gray-50 transition" onClick={() => window.location.href='/dashboard/reports/discounts-deductions'}>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Discount</p>
          <h2 className="text-3xl font-black text-orange-500">{currency}{Number(String(invoiceStats?.total_discount || 0).replace(/,/g, "")).toLocaleString()}</h2>
          <p className="text-[10px] text-gray-400 font-bold uppercase mt-1 tracking-widest flex items-center gap-1">View Deductions Report <IconTrendingUp size={10} /></p>
        </div>
        <div className="p-6 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] shadow-sm cursor-pointer hover:bg-gray-50 transition" onClick={() => window.location.href='/dashboard/reports/voided-invoices'}>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Voided Invoices</p>
          <h2 className="text-3xl font-black text-red-600">{invoiceStats?.voided_count || 0}</h2>
          <p className="text-[10px] text-gray-400 font-bold uppercase mt-1 tracking-widest flex items-center gap-1">View Report <IconTrendingUp size={10} /></p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Sales Status Pie Chart */}
        <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm">
          <h3 className="font-black text-xl mb-6 flex items-center gap-2"><IconReceipt2 /> Sales Status Breakdown</h3>
          <StatusPieChart 
            data={[invoiceStats?.paid_count, invoiceStats?.partial_count, invoiceStats?.unpaid_count]} 
            labels={['Fully Paid', 'Partially Paid', 'Unpaid']}
            colors={['#22c55e', '#3b82f6', '#ef4444']}
          />
        </div>

        {/* Payment Methods Chart */}
        <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm">
          <h3 className="font-black text-xl mb-6 flex items-center gap-2"><IconWallet /> Payments by Method</h3>
          <ReportPaymentTypePieChart paymentTypes={totalPaymentsByPaymentTypes} />
        </div>

        {/* Purchase Summary Chart */}
        <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm">
          <h3 className="font-black text-xl mb-6 flex items-center gap-2"><IconTrendingDown /> Purchase Liabilities</h3>
          <StatusPieChart 
            data={[purchaseStats?.total_paid_purchase, purchaseStats?.total_due_purchase]} 
            labels={['Paid to Suppliers', 'Due to Suppliers']}
            colors={['#10b981', '#f43f5e']}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Top Selling Items */}
        <div className='col-span-1 lg:col-span-1 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] overflow-hidden shadow-sm'>
          <div className='p-8 pb-4'>
            <h3 className='font-black text-xl'>{t("reports.top_selling_items")}</h3>
          </div>
          <div className="p-8 pt-0 h-96 overflow-y-auto">
            {topSellingItems?.length === 0 ? (
                <div className="text-center py-10 opacity-50">No data found</div>
            ) : (
                <div className="space-y-4">
                    {topSellingItems.map((item, i) => (
                        <div key={i} className="flex items-center gap-4 p-3 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            <div className="w-10 h-10 bg-restro-gray rounded-xl flex items-center justify-center text-gray-500">
                                <IconCarrot size={20} />
                            </div>
                            <div className="flex-1">
                                <p className="font-bold text-sm truncate">{item.title}</p>
                                <p className="text-xs text-gray-400">{currency}{item.price}</p>
                            </div>
                            <div className="text-right">
                                <p className="font-black">{item.orders_count}</p>
                                <p className="text-[10px] text-gray-400 uppercase font-black tracking-tighter">Sold</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
          </div>
        </div>

        {/* Detailed Metrics */}
        <div className="col-span-1 lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">{t("reports.avg_order_value")}</p>
                <h3 className="text-4xl font-black">{currency}{Number(String(averageOrderValue || 0).replace(/,/g, "")).toFixed(2)}</h3>
            </div>
            <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">{t("reports.total_customers")}</p>
                <h3 className="text-4xl font-black">{totalCustomers}</h3>
            </div>
            <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">New vs Repeat</p>
                <div className="flex items-baseline gap-4 mt-2">
                    <div>
                        <p className="text-3xl font-black text-restro-green">{newCustomers}</p>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">New</p>
                    </div>
                    <div className="h-8 w-[1px] bg-gray-200 dark:bg-gray-800"></div>
                    <div>
                        <p className="text-3xl font-black text-blue-600">{repeatedCustomers}</p>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Repeat</p>
                    </div>
                </div>
            </div>
            <div className="p-8 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2.5rem] shadow-sm flex flex-col justify-center">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-bold text-gray-400">{t("reports.tax")}</span>
                    <span className="font-black">{currency}{taxTotal}</span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-400">{t("reports.service_charge_total")}</span>
                    <span className="font-black">{currency}{serviceChargeTotal}</span>
                </div>
            </div>
        </div>
      </div>

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className = 'modal-box border border-restro-border-green dark:rounded-3xl p-0 overflow-hidden'>
          <div className="bg-gray-900 p-8 text-white">
            <h3 className = "font-black text-2xl flex items-center gap-2">
                <IconFilter stroke={2} /> {t("reports.filter")}
            </h3>
            <p className="text-gray-400 text-sm mt-1">Select date range for analytics</p>
          </div>

          <div className="p-8 space-y-6">
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.filter_label")}</label>
              <select
                className='select select-bordered w-full rounded-2xl bg-gray-50 dark:bg-gray-900 border-none'
                ref={filterTypeRef}
                defaultValue={state.filter}
              >
                {filters.map((filter, index) => (
                  <option key={index} value={filter.key}>
                    {filter.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.from")}</label>
                <input
                  defaultValue={defaultDateFrom}
                  type="date"
                  ref={fromDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
              <div className="form-control">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">{t("reports.to")}</label>
                <input
                  defaultValue={defaultDateTo}
                  type="date"
                  ref={toDateRef}
                  className='bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm'
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button onClick={() => document.getElementById("filter-dialog").close()} className="flex-1 py-4 font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-2xl transition active:scale-95">{t("reports.close")}</button>
              <button
                onClick={() => {
                  setState({
                    ...state,
                    filter: filterTypeRef.current.value,
                    fromDate: fromDateRef.current.value || null,
                    toDate: toDateRef.current.value || null,
                  });
                }}
                className = 'flex-[1.5] py-4 font-bold text-white bg-restro-green rounded-2xl hover:bg-restro-green-button-hover transition active:scale-95 shadow-lg shadow-green-100 dark:shadow-none'
              >
                {t("reports.apply")}
              </button>
            </div>
          </div>
        </div>
      </dialog>
      </ReportExportWrapper>
    </Page>
  );
}
